import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { LogOut, User, Settings, ChevronDown, Moon, Sun, Menu, X, Sparkles } from 'lucide-react'
import { useAuth } from '../../hooks/useAuthHook'
import { Link as RouterLink } from 'react-router-dom'
import { navigation, adminNavigation } from './Sidebar'
import { useTenantSettings } from '../../hooks/useTenantSettingsHook'

export function Header() {
  const { user, logout } = useAuth()
  const { tenant, settings } = useTenantSettings()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isDark, setIsDark] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    if (isDark) document.documentElement.classList.add('dark')
    else document.documentElement.classList.remove('dark')
  }, [isDark])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = () => setIsDropdownOpen(false)
    if (isDropdownOpen) {
      document.addEventListener('click', handleClick)
      return () => document.removeEventListener('click', handleClick)
    }
  }, [isDropdownOpen])

  return (
    <header className="sticky top-0 z-40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl shadow-soft border-b border-gray-200 dark:border-gray-800">
      <div className="px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Left side - Logo & Brand */}
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-500/10 text-gray-700 dark:text-gray-200 transition-all"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2 group">
              {typeof settings?.branding_logo_url === 'string' && settings.branding_logo_url ? (
                <img
                  src={settings.branding_logo_url as string}
                  alt="Logo"
                  className="h-8 w-8 rounded-xl object-cover ring-2 ring-primary-500/20 group-hover:ring-primary-500/40 transition-all"
                />
              ) : (
                <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
              )}
              <div className="hidden sm:block">
                <div className="text-lg font-bold text-gradient-primary">
                  {tenant?.name || 'StudioManager'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
                  Studio Management
                </div>
              </div>
            </Link>

            {/* Role Badge */}
            {user?.role === 'SuperAdmin' && (
              <span className="hidden lg:inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-full shadow-sm">
                <Sparkles className="h-3 w-3" />
                Super Admin
              </span>
            )}
            {user?.role === 'Admin' && (
              <span className="hidden lg:inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold badge-primary">
                Admin
              </span>
            )}
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-2">
            {/* Dark mode toggle */}
            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2.5 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-500/10 text-gray-700 dark:text-gray-200 transition-all"
              aria-label="Toggle theme"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <Sun className="h-5 w-5 text-primary-500" />
              ) : (
                <Moon className="h-5 w-5 text-primary-600" />
              )}
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsDropdownOpen(!isDropdownOpen)
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-500/10 text-gray-700 dark:text-gray-200 transition-all"
              >
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt="Avatar"
                    className="h-7 w-7 rounded-full object-cover ring-2 ring-primary-500/20"
                  />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                )}
                <span className="hidden md:block font-medium text-sm">{user?.name}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-900 rounded-2xl shadow-soft-lg border border-gray-200 dark:border-gray-800 overflow-hidden animate-scale-in">
                  {/* User info */}
                  <div className="px-4 py-3 bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-500/10 dark:to-accent-500/10 border-b border-gray-200 dark:border-gray-800">
                    <div className="font-semibold text-gray-900 dark:text-gray-100">{user?.name}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">{user?.email}</div>
                    <div className="mt-1 inline-block">
                      <span className="badge-primary text-xs">{user?.role}</span>
                    </div>
                  </div>

                  {/* Menu items */}
                  <div className="py-1">
                    <Link
                      to="/profile"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <User className="h-4 w-4 text-primary-500" />
                      Profile
                    </Link>
                    <Link
                      to="/settings"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <Settings className="h-4 w-4 text-primary-500" />
                      Settings
                    </Link>
                    <div className="border-t border-gray-200 dark:border-gray-800 my-1" />
                    <button
                      onClick={() => {
                        setIsDropdownOpen(false)
                        logout()
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-500/10 transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile navigation drawer */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden animate-fade-in"
            onClick={() => setMobileOpen(false)}
          />

          {/* Drawer */}
          <div className="fixed left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white dark:bg-gray-900 shadow-soft-lg z-50 md:hidden animate-slide-down">
            {/* Drawer header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <span className="text-lg font-bold text-gradient-primary">Menu</span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Navigation items */}
            <nav className="p-4 space-y-1 custom-scrollbar overflow-y-auto" style={{ maxHeight: 'calc(100vh - 80px)' }}>
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 mb-2">
                Main
              </div>
              {navigation.map((item) => {
                const Icon = item.icon
                return (
                  <RouterLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-all font-medium"
                  >
                    <Icon className="h-5 w-5 text-primary-500" />
                    {item.name}
                  </RouterLink>
                )
              })}

              {adminNavigation.length > 0 && (
                <>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 mb-2 mt-4">
                    Admin
                  </div>
                  {adminNavigation.map((item) => {
                    const Icon = item.icon
                    return (
                      <RouterLink
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-accent-50 dark:hover:bg-accent-500/10 transition-all font-medium"
                      >
                        <Icon className="h-5 w-5 text-accent-500" />
                        {item.name}
                      </RouterLink>
                    )
                  })}
                </>
              )}
            </nav>
          </div>
        </>
      )}
    </header>
  )
}
